import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  primeAccessService,
  type PrimeAccess,
  type PrimeAccessBenefits,
  type PrimeAccessWithBenefits,
  type PrimeAccessPurchaseRequest,
  type PrimeAccessPurchaseResponse,
} from '@/services/prime-access.service';

/**
 * Query key factory for prime access related queries
 */
export const primeAccessKeys = {
  all: ['prime-access'] as const,
  plans: (filters?: { status?: 'ACTIVE' | 'INACTIVE' }) =>
    [...primeAccessKeys.all, 'plans', filters] as const,
  plan: (id: string) => [...primeAccessKeys.all, 'plan', id] as const,
  benefits: (planId: string) => [...primeAccessKeys.all, 'benefits', planId] as const,
  userActivePlan: () => [...primeAccessKeys.all, 'user-active'] as const,
  userPurchases: () => [...primeAccessKeys.all, 'user-purchases'] as const,
} as const;

/**
 * Hook to fetch all active Prime Access plans
 * @param params - Optional filter parameters
 * @param options - React Query options
 */
export function usePrimeAccessPlans(
  params?: { status?: 'ACTIVE' | 'INACTIVE'; tier?: 'GENERAL' | 'ASETRACLUB' },
  options?: Omit<UseQueryOptions<PrimeAccess[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: primeAccessKeys.plans(params),
    queryFn: () => primeAccessService.getPlans(params),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch a single Prime Access plan by ID
 * @param planId - Plan ID
 * @param options - React Query options
 */
export function usePrimeAccessPlan(
  planId: string,
  options?: Omit<UseQueryOptions<PrimeAccess, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: primeAccessKeys.plan(planId),
    queryFn: () => primeAccessService.getPlan(planId),
    enabled: !!planId,
    ...options,
  });
}

/**
 * Hook to fetch Prime Access benefits by plan ID
 * @param planId - Plan ID
 * @param options - React Query options
 */
export function usePrimeAccessBenefits(
  planId: string,
  options?: Omit<UseQueryOptions<PrimeAccessBenefits, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: primeAccessKeys.benefits(planId),
    queryFn: () => primeAccessService.getBenefits(planId),
    enabled: !!planId,
    ...options,
  });
}

/**
 * Hook to fetch user's active Prime Access plan with benefits
 * @param options - React Query options
 */
export function useUserActivePlan(
  options?: Omit<UseQueryOptions<PrimeAccessWithBenefits | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: primeAccessKeys.userActivePlan(),
    queryFn: () => primeAccessService.getUserActivePlan(),
    staleTime: 1000 * 60, // Cache for 1 minute
    ...options,
  });
}

/**
 * Hook to fetch user's Prime Access purchase history
 * @param options - React Query options
 */
export function useUserPrimeAccessPurchases(
  options?: Omit<
    UseQueryOptions<
      Array<{
        id: string;
        membershipId: string;
        membershipName: string;
        membershipTier: string;
        amount: string;
        txHash: string;
        status: string;
        createdAt: number;
        invoiceNo?: string;
        biAccessStatus?: string;
        biAccessStartAt?: number;
        biAccessEndAt?: number;
      }>,
      Error
    >,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: primeAccessKeys.userPurchases(),
    queryFn: () => primeAccessService.getUserPurchases(),
    ...options,
  });
}

/**
 * Hook to purchase Prime Access with wallet signature (gas relay)
 * @param options - Mutation options
 */
export function usePurchasePrimeAccess(options?: {
  onSuccess?: (data: PrimeAccessPurchaseResponse) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PrimeAccessPurchaseRequest) => primeAccessService.purchase(data),
    onSuccess: data => {
      // Invalidate user's active plan and purchases to refetch
      queryClient.invalidateQueries({ queryKey: primeAccessKeys.userActivePlan() });
      queryClient.invalidateQueries({ queryKey: primeAccessKeys.userPurchases() });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
